/**
 * SOFSEK — shared shell for the read-only relational reports (§17.E Reports
 * group; the new-app home for the legacy westlock "Reports" screens). Each
 * report resolves its own data to display strings, then hands the rows here;
 * the shell owns the common harness: personalization wiring (so the screen
 * smoke test's hard gate is satisfied), a free-text search + optional per-field
 * facets + Clear, a Refresh, and the grid. Reports are read-only by design —
 * no create/edit — so there is no modal or write path here.
 */
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthSession } from '../../../types/auth';
import { EmptyState } from '../../../components/common/EmptyState';
import { FacetFilter } from '../../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../../components/common/YesNoBadge';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../../components/personalization';
import type { GridColumnDef } from '../../../components/personalization';

export type ReportCellKind = 'text' | 'mono' | 'yesno';
export interface ReportColumn { key: string; header: string; width?: number; kind?: ReportCellKind }
export interface ReportFacetDef { key: string; label: string; options: { value: string; label: string }[] }
/** A row is a bag of pre-resolved display strings keyed by column key, plus a
 *  unique `id` used only as the React key (never a rendered column). */
export type ReportRow = { id: string } & Record<string, string>;

export function tlName(tl: { locale_code: string; display_text: string }[] | undefined, loc: string): string {
  const rows = tl ?? [];
  return rows.find(x => x.locale_code === loc)?.display_text ?? rows.find(x => x.locale_code === 'EN_US')?.display_text ?? '';
}

const btnCls = 'h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt';

interface ShellProps {
  pageCode: string;
  session: AuthSession;
  title: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  searchKeys: string[];
  facets?: ReportFacetDef[];
  busy: boolean;
  error: string | null;
  onRefresh: () => void;
  /** §17.B13 — optional per-row related-children provider; a report with real
   *  child collections supplies this, otherwise rows render an inert control. */
  relatedGroups?: (row: ReportRow) => RelatedChildGroup[];
}

function ReportInner({ title, columns, rows, searchKeys, facets = [], busy, error, onRefresh, relatedGroups }: Omit<ShellProps, 'pageCode' | 'session'>) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');
  const detail = useExpandedRows<string>();
  const groupsFor = (r: ReportRow) => (relatedGroups?.(r) ?? []);
  const hasRelated = (r: ReportRow) => groupsFor(r).some(g => g.items.length > 0);
  // Default any Active (is_active) facet to 'Y' — show active rows first.
  const [facetSel, setFacetSel] = useState<Record<string, string[]>>(
    () => Object.fromEntries(facets.filter(f => f.key === 'is_active').map(f => [f.key, ['Y']])),
  );

  const gridCols: GridColumnDef[] = useMemo(
    () => columns.map(c => ({ key: c.key, header: c.header, defaultWidth: c.width ?? 160 })),
    [columns],
  );
  const kindByKey = useMemo(() => Object.fromEntries(columns.map(c => [c.key, c.kind ?? 'text'])), [columns]);

  const anyFacetActive = Object.values(facetSel).some(v => v.length > 0);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r => {
      if (q && !searchKeys.some(k => (r[k] ?? '').toLowerCase().includes(q))) return false;
      for (const f of facets) {
        const sel = facetSel[f.key];
        if (sel && sel.length > 0 && !sel.includes(r[f.key])) return false;
      }
      return true;
    });
  }, [rows, filter, facetSel, facets, searchKeys]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft">{title}</span>
        <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
          className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none" />
        {facets.map(f => (
          <FacetFilter key={f.key} label={f.label} options={f.options}
            selected={facetSel[f.key] ?? []}
            onChange={vals => setFacetSel(prev => ({ ...prev, [f.key]: vals }))} />
        ))}
        <ClearFiltersButton active={filter.trim() !== '' || anyFacetActive} onClear={() => { setFilter(''); setFacetSel({}); }} />
        <button onClick={onRefresh} className={btnCls}>{t('common.refresh', 'Refresh')}</button>
        {busy && <span className="text-csm-xs text-csm-watermark">{t('common.loading', 'Loading…')}</span>}
        {error && <span className="text-csm-xs text-csm-danger">{error}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={gridCols}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(r.id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {kindByKey[col.key] === 'yesno'
                          ? <YesNoBadge value={(r[col.key] === 'Y' ? 'Y' : 'N')} />
                          : kindByKey[col.key] === 'mono'
                            ? <span className="font-mono text-csm-sm">{r[col.key]}</span>
                            : r[col.key]}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.id) && hasRelated(r) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={groupsFor(r)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
      </div>
    </div>
  );
}

export function SfkReportShell({ pageCode, session, ...rest }: ShellProps) {
  return (
    <PersonalizationProvider pageCode={pageCode} session={session}>
      <ReportInner {...rest} />
    </PersonalizationProvider>
  );
}
